import { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  DarkMode,
  Flex,
  Grid,
  IconButton,
  Image,
  Modal,
  ModalContent,
  ModalOverlay,
  Spinner,
  Text,
  VStack,
  useToast,
} from '@chakra-ui/react';
import {
  ChevronLeft,
  ChevronRight,
  Download,
  FolderPlus,
  MessageCircle,
  Star,
  X,
} from 'lucide-react';
import { savedToast } from '../SavedToast';
import { usePinchZoom } from '../../hooks/usePinchZoom';
import { fetchWithProgress, saveBlob } from '../../lib/download';
import {
  fetchDecryptedBlob,
  mediaSecrets,
  useMediaUrl,
} from '../../lib/e2ee/media';
import {
  formatBytes,
  type ChatMessage,
  type MessageMetadata,
} from './messageUtils';
import { appLocale } from '../../lib/format';
import { t as tNow, useT } from '../../i18n';

interface ImageLightboxProps {
  message: ChatMessage | null;
  isOwn: boolean;
  /** The chat it was sent in ("Everyone"). */
  chatTitle: string;
  onClose: () => void;
  onSaveDocument: (message: ChatMessage) => void;
  onToggleStar: (message: ChatMessage) => void;
  onShowInChat: (message: ChatMessage) => void;
  /**
   * An album (M60, D219): `message` is the item shown, `index` its place;
   * the viewer pages through the rest.
   */
  album?: { count: number; index: number; onIndex: (index: number) => void };
}

/** The item a swipe or arrow lands on, staying inside the album. */
export function pageTo(index: number, step: number, count: number): number {
  return Math.min(count - 1, Math.max(0, index + step));
}

// A video in an album, decrypted on the device once opened.
function ViewerVideo({ message }: { message: ChatMessage }) {
  const secrets = mediaSecrets(message);
  const meta = (message.metadata ?? {}) as MessageMetadata;
  const poster = useMediaUrl(meta.thumbnailUrl, secrets.thumb, 'image/jpeg');
  const video = useMediaUrl(message.content, secrets.file, secrets.mimeType);
  if (!video.src) return <Spinner color="white" />;
  return (
    <video
      src={video.src}
      poster={poster.src}
      controls
      playsInline
      style={{ maxWidth: '100%', maxHeight: '100%' }}
      onClick={(event) => event.stopPropagation()}
    />
  );
}

// "Today, 12:40".
function whenLabel(iso: string, now = new Date()): string {
  const date = new Date(iso);
  const time = date.toLocaleTimeString(appLocale(), {
    hour: 'numeric',
    minute: '2-digit',
  });
  if (date.toDateString() === now.toDateString())
    return tNow('chat.call.today', { time });
  return `${date.toLocaleDateString(appLocale(), { day: 'numeric', month: 'short' })}, ${time}`;
}

function ViewerAction({
  label,
  icon,
  pressed,
  busy,
  onClick,
}: {
  label: string;
  icon: React.ReactElement;
  pressed?: boolean;
  busy?: boolean;
  onClick: () => void;
}) {
  return (
    <VStack
      as="button"
      type="button"
      spacing="6px"
      minH="64px"
      fontSize="13px"
      fontWeight={600}
      aria-pressed={pressed}
      onClick={onClick}
      disabled={busy}
    >
      <Flex
        w="48px"
        h="48px"
        borderRadius="full"
        bg="#211E1B"
        align="center"
        justify="center"
      >
        {busy ? <Spinner size="sm" /> : icon}
      </Flex>
      <Text as="span">{label}</Text>
    </VStack>
  );
}

// Photo viewer (design: "Photo viewer · after download"): who sent it and
// where, the photo edge to edge, and what you can do with it underneath.
export function ImageLightbox({
  message,
  isOwn,
  chatTitle,
  onClose,
  onSaveDocument,
  onToggleStar,
  onShowInChat,
  album,
}: ImageLightboxProps) {
  const toast = useToast();
  const t = useT();
  const [downloading, setDownloading] = useState(false);
  const meta = (message?.metadata ?? {}) as MessageMetadata;

  // The chat shows a small preview (#2); the full photo streams in here with
  // a ring and "1.8 of 2.9 MB" (Living Room "Downloading"). Cancel stops it
  // and offers "Download · 2.9 MB" instead.
  const [full, setFull] = useState<{ blob: Blob; url: string } | null>(null);
  const [progress, setProgress] = useState<{ loaded: number; total: number }>({
    loaded: 0,
    total: 0,
  });
  const [cancelled, setCancelled] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const abort = useRef<AbortController | null>(null);
  const messageId = message?.id;
  const secrets = message ? mediaSecrets(message) : {};
  const preview = useMediaUrl(meta.thumbnailUrl, secrets.thumb, 'image/jpeg');
  const isVideo = message?.type === 'video';
  // A video plays in its own element; only photos are fetched here.
  const source = isVideo ? undefined : message?.content;
  const touchStart = useRef<number | null>(null);
  // Pinch and double-tap zoom (#85); a new photo starts unzoomed.
  const pinch = usePinchZoom();
  const { reset: resetZoom } = pinch;
  useEffect(() => resetZoom(), [messageId, album?.index, resetZoom]);
  const page = (step: number) => {
    if (!album) return;
    const next = pageTo(album.index, step, album.count);
    if (next !== album.index) album.onIndex(next);
  };
  useEffect(() => {
    if (!album || !message) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'ArrowLeft') page(-1);
      if (event.key === 'ArrowRight') page(1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  useEffect(() => {
    setFull(null);
    setCancelled(false);
    setProgress({ loaded: 0, total: meta.fileSize ?? 0 });
    if (!source) return;
    const controller = new AbortController();
    abort.current = controller;
    let objectUrl: string | null = null;
    const options = {
      signal: controller.signal,
      sizeHint: meta.fileSize,
      onProgress: (loaded: number, total: number) =>
        setProgress({ loaded, total }),
    };
    // Encrypted photos are decrypted here once they've arrived (M28).
    (secrets.file
      ? fetchDecryptedBlob(source, secrets.file, secrets.mimeType, options)
      : fetchWithProgress(source, options)
    )
      .then((blob) => {
        objectUrl = URL.createObjectURL(blob);
        setFull({ blob, url: objectUrl });
      })
      .catch(() => {
        if (!controller.signal.aborted) setCancelled(true);
      });
    return () => {
      controller.abort();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
    // Re-run per photo and on "Download" after a cancel; metadata is read once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messageId, source, attempt, album?.index]);

  const cancel = () => {
    abort.current?.abort();
    setCancelled(true);
  };

  // Save the photo under its name; reuses what the viewer already fetched.
  const download = async () => {
    if (!message) return;
    setDownloading(true);
    try {
      const blob =
        full?.blob ??
        (secrets.file
          ? await fetchDecryptedBlob(
              message.content,
              secrets.file,
              secrets.mimeType
            )
          : await fetchWithProgress(message.content));
      saveBlob(
        blob,
        meta.fileName ?? `twinden-photo-${message.id.slice(0, 8)}.jpg`
      );
      savedToast(toast, t('chat.viewer.saved'), { position: 'top' });
    } catch {
      toast({ title: t('chat.viewer.couldNotDownload'), status: 'error' });
    } finally {
      setDownloading(false);
    }
  };

  const percent = progress.total
    ? Math.round((progress.loaded / progress.total) * 100)
    : undefined;
  const sizeLabel = progress.total
    ? t('chat.file.progress', {
        done: formatBytes(progress.loaded),
        total: formatBytes(progress.total),
      })
    : formatBytes(progress.loaded);

  return (
    <Modal
      isOpen={message !== null}
      onClose={onClose}
      size="full"
      motionPreset="none"
    >
      <ModalOverlay bg="#0B0A09" />
      <DarkMode>
        <ModalContent
          data-theme="dark"
          bg="#0B0A09"
          color="#F3F0EB"
          m={0}
          h="var(--app-height, 100vh)"
          borderRadius={0}
          display="flex"
          flexDirection="column"
          pt="env(safe-area-inset-top)"
        >
          {message && (
            <>
              <Flex h="60px" align="center" gap={2} px={2} flexShrink={0}>
                <IconButton
                  aria-label={t('common.close')}
                  icon={<X size={22} />}
                  colorScheme="gray"
                  bg="#211E1B"
                  color="#F3F0EB"
                  _hover={{ bg: '#2A2724' }}
                  onClick={onClose}
                />
                <Box flex="1" minW={0}>
                  <Text fontWeight={700} fontSize="16px" noOfLines={1}>
                    {isOwn
                      ? t('common.you')
                      : (message.sender?.displayName ?? t('chat.unknown'))}
                  </Text>
                  <Text fontSize="13px" color="#ABA49A" noOfLines={1}>
                    {whenLabel(message.createdAt)} · {chatTitle}
                  </Text>
                </Box>
                {album && album.count > 1 && (
                  <Text
                    fontSize="14px"
                    fontWeight={600}
                    color="#ABA49A"
                    pr={2}
                    flexShrink={0}
                    data-testid="viewer-position"
                  >
                    {t('chat.viewer.position', {
                      n: album.index + 1,
                      total: album.count,
                    })}
                  </Text>
                )}
              </Flex>

              <Flex
                flex="1"
                minH={0}
                align="center"
                justify="center"
                position="relative"
                overflow="hidden"
                // The browser mustn't zoom or scroll the page here: the photo
                // zooms itself (#85). Measured on this box, which never scales.
                sx={{ touchAction: 'none' }}
                {...(isVideo ? {} : pinch.handlers)}
                onClick={() => {
                  if (!pinch.gestured()) onClose();
                }}
                onTouchStart={(event) => {
                  touchStart.current = event.touches[0]?.clientX ?? null;
                }}
                onTouchEnd={(event) => {
                  const start = touchStart.current;
                  const end = event.changedTouches[0]?.clientX;
                  touchStart.current = null;
                  if (start === null || end === undefined) return;
                  // A zoomed photo pans instead of paging.
                  if (pinch.zoomed || pinch.gestured()) return;
                  if (Math.abs(end - start) > 50) page(end < start ? 1 : -1);
                }}
              >
                {album && album.index > 0 && (
                  <IconButton
                    aria-label={t('chat.viewer.previous')}
                    icon={<ChevronLeft size={24} />}
                    position="absolute"
                    left={2}
                    zIndex={1}
                    colorScheme="gray"
                    bg="rgba(33, 30, 27, 0.8)"
                    color="#F3F0EB"
                    onClick={(event) => {
                      event.stopPropagation();
                      page(-1);
                    }}
                  />
                )}
                {album && album.index < album.count - 1 && (
                  <IconButton
                    aria-label={t('chat.viewer.next')}
                    icon={<ChevronRight size={24} />}
                    position="absolute"
                    right={2}
                    zIndex={1}
                    colorScheme="gray"
                    bg="rgba(33, 30, 27, 0.8)"
                    color="#F3F0EB"
                    onClick={(event) => {
                      event.stopPropagation();
                      page(1);
                    }}
                  />
                )}
                {isVideo ? (
                  <ViewerVideo key={album?.index} message={message} />
                ) : full ? (
                  <Image
                    src={full.url}
                    alt={meta.fileName ?? t('chat.type.image')}
                    maxW="full"
                    maxH="full"
                    objectFit="contain"
                    draggable={false}
                    data-testid="viewer-photo"
                    style={pinch.style}
                    onClick={(event) => event.stopPropagation()}
                  />
                ) : (
                  <Box
                    position="relative"
                    maxW="full"
                    maxH="full"
                    onClick={(event) => event.stopPropagation()}
                  >
                    {preview.src && (
                      <Image
                        src={preview.src}
                        alt=""
                        maxW="100vw"
                        maxH="calc(var(--app-height, 100vh) - 220px)"
                        objectFit="contain"
                        opacity={0.6}
                        sx={
                          meta.width && meta.height
                            ? { aspectRatio: `${meta.width} / ${meta.height}` }
                            : undefined
                        }
                        w={meta.width ? `${meta.width}px` : undefined}
                      />
                    )}
                    <Flex
                      position={preview.src ? 'absolute' : 'static'}
                      inset={0}
                      direction="column"
                      align="center"
                      justify="center"
                      gap="6px"
                    >
                      {cancelled ? (
                        <Button
                          h="40px"
                          px="14px"
                          colorScheme="gray"
                          bg="rgba(31, 29, 27, 0.75)"
                          color="white"
                          leftIcon={<Download size={18} />}
                          _hover={{ bg: 'rgba(31, 29, 27, 0.9)' }}
                          onClick={() => setAttempt((n) => n + 1)}
                        >
                          {progress.total
                            ? formatBytes(progress.total)
                            : t('chat.viewer.download')}
                        </Button>
                      ) : (
                        <>
                          <Box position="relative" w="52px" h="52px">
                            <CircularProgress
                              value={percent}
                              isIndeterminate={percent === undefined}
                              size="52px"
                              thickness="8px"
                              color="saffron"
                              trackColor="rgba(31, 29, 27, 0.55)"
                              aria-label={t('chat.viewer.downloading')}
                            />
                            <IconButton
                              aria-label={t('chat.file.cancel')}
                              icon={<X size={18} />}
                              position="absolute"
                              inset="4px"
                              minW={0}
                              w="44px"
                              h="44px"
                              colorScheme="gray"
                              bg="transparent"
                              color="white"
                              _hover={{ bg: 'whiteAlpha.200' }}
                              onClick={cancel}
                            />
                          </Box>
                          <Text
                            fontSize="12px"
                            fontWeight={700}
                            color="#1F1D1B"
                            bg="rgba(250, 249, 247, 0.85)"
                            px={2}
                            py="2px"
                            borderRadius="8px"
                          >
                            {sizeLabel || t('chat.file.starting')}
                          </Text>
                        </>
                      )}
                    </Flex>
                  </Box>
                )}
              </Flex>

              <Grid
                templateColumns={`repeat(${album ? 3 : 4}, minmax(0, 1fr))`}
                px={2}
                pt={3}
                pb="max(env(safe-area-inset-bottom), 24px)"
                maxW="520px"
                w="full"
                mx="auto"
                flexShrink={0}
              >
                <ViewerAction
                  label={t('chat.viewer.download')}
                  icon={<Download size={22} />}
                  busy={downloading}
                  onClick={() => void download()}
                />
                {/* An album item's file isn't the message's first one, which
                    is what Documents takes from a message (not yet, M60). */}
                {!album && (
                  <ViewerAction
                    label={t('chat.viewer.toDocuments')}
                    icon={<FolderPlus size={22} />}
                    onClick={() => onSaveDocument(message)}
                  />
                )}
                <ViewerAction
                  label={t(
                    message.starredByMe
                      ? 'chat.viewer.starred'
                      : 'chat.viewer.star'
                  )}
                  pressed={message.starredByMe}
                  icon={
                    <Star
                      size={22}
                      fill={message.starredByMe ? '#F0B63F' : 'none'}
                      color={message.starredByMe ? '#F0B63F' : 'currentColor'}
                    />
                  }
                  onClick={() => onToggleStar(message)}
                />
                <ViewerAction
                  label={t('chat.viewer.goToChat')}
                  icon={<MessageCircle size={22} />}
                  onClick={() => onShowInChat(message)}
                />
              </Grid>
            </>
          )}
        </ModalContent>
      </DarkMode>
    </Modal>
  );
}
